import { Component, inject, input, signal, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EvidenceSection, EvidenceImage, EvidenceStatus } from '../../../models/applicant-record/evidence.model';
import { TicketService } from '../../../services/data/ticket.service';

export type { EvidenceSection, EvidenceImage, EvidenceStatus };

@Component({
  selector: 'app-photo-evidence',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './photo-evidence.component.html',
  styleUrl: './photo-evidence.component.css',
})
export class PhotoEvidenceComponent implements OnDestroy {
  private readonly ticketService = inject(TicketService);
  private readonly timeouts: ReturnType<typeof setTimeout>[] = [];

  // Inputs
  sections = input<EvidenceSection[]>([]);
  loading = input(false);

  // Signals - State
  expandedSections = signal<Set<string>>(new Set());
  previewImage = signal<EvidenceImage | null>(null);
  previewUrl = signal<string | null>(null);
  previewLoading = signal(false);
  thumbnailCache = signal<Map<string, string>>(new Map());
  sectionLoadingImages = signal<Map<string, boolean>>(new Map());
  sectionsInitialized = signal(false);

  constructor() {
    effect(() => {
      const sections = this.sections();
      if (sections.length > 0 && !this.sectionsInitialized()) {
        this.sectionsInitialized.set(true);
        this.expandAllSections();
      }
    });
  }

  ngOnDestroy(): void {
    this.timeouts.forEach(t => clearTimeout(t));
    this.revokePreviewUrl();
    this.thumbnailCache().forEach(url => URL.revokeObjectURL(url));
  }

  // region Public API
  public toggleSection(sectionId: string): void {
    const current = new Set(this.expandedSections());
    current.has(sectionId) ? current.delete(sectionId) : current.add(sectionId);
    this.expandedSections.set(current);
    if (current.has(sectionId)) {
      this.sections().find(s => s.id === sectionId)?.images.forEach(img => this.loadThumbnail(img));
    }
  }

  public expandAllSections(): void {
    this.sections().forEach((section, i) => {
      const timeout = setTimeout(() => {
        const current = new Set(this.expandedSections());
        if (!current.has(section.id)) {
          current.add(section.id);
          this.expandedSections.set(current);
          section.images.forEach(img => this.loadThumbnail(img));
        }
      }, i * 200);
      this.timeouts.push(timeout);
    });
  }

  public isExpanded(sectionId: string): boolean {
    return this.expandedSections().has(sectionId);
  }

  public isSectionLoading(sectionId: string): boolean {
    const section = this.sections().find(s => s.id === sectionId);
    return section?.images.some(img => !this.thumbnailCache().has(img.id)) ?? false;
  }

  public getThumbnailUrl(image: EvidenceImage): string | null {
    return this.thumbnailCache().get(image.id) ?? null;
  }

  public getImageUrl(image: EvidenceImage): string | null {
    return this.thumbnailCache().get(image.id)
      ?? image.thumbnailUrl
      ?? (image.thumbnailBase64 ? `data:${image.contentType || 'image/png'};base64,${image.thumbnailBase64}` : null)
      ?? this.getFullImageUrl(image);
  }

  public onImageClick(image: EvidenceImage): void {
    this.previewImage.set(image);
    this.previewLoading.set(true);
    this.previewUrl.set(null);

    this.fetchFile(this.getFullImageUrl(image), image).then(file => {
      this.previewUrl.set(URL.createObjectURL(file));
      this.previewLoading.set(false);
    }).catch(() => this.previewLoading.set(false));
  }

  public onDownload(image: EvidenceImage, event: Event): void {
    event.stopPropagation();
    this.fetchFile(image.fullUrl ?? null, image).then(() => {
      const link = document.createElement('a');
      link.href = `data:${image.contentType || 'image/png'};base64,${image.fullBase64}`;
      link.download = image.fileName || `evidencia-${image.id}`;
      link.click();
    });
  }

  public onImageError(event: Event, image: EvidenceImage): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    img.parentElement?.appendChild(Object.assign(document.createElement('div'), {
      className: 'w-full h-36 flex flex-col items-center justify-center text-(--color-text-muted) bg-(--color-bg-secondary)',
      innerHTML: `<span class="text-5xl font-bold text-start">${image.type || 'IMG'}</span>`,
    }));
  }

  public closePreview(): void {
    this.revokePreviewUrl();
    this.previewImage.set(null);
    this.previewUrl.set(null);
  }


  // region Private
  private getFullImageUrl(image: EvidenceImage): string | null {
    return image.fullUrl ?? (image.fullBase64 ? `data:${image.contentType || 'image/png'};base64,${image.fullBase64}` : null);
  }

  private async fetchFile(url: string | null, image: EvidenceImage): Promise<Blob> {
    if (!url) throw new Error('No URL');
    const fileName = image.fileName || `evidencia-${image.id}`;
    return this.ticketService.getFile(url, fileName, image.contentType || 'image/png', image.md5 || '');
  }

  private loadThumbnail(image: EvidenceImage): void {
    if (this.thumbnailCache().has(image.id)) return;
    if (image.thumbnailBase64) {
      this.thumbnailCache.set(new Map(this.thumbnailCache()).set(image.id, `data:${image.contentType || 'image/png'};base64,${image.thumbnailBase64}`));
      return;
    }
    if (image.thumbnailUrl) {
      this.thumbnailCache.set(new Map(this.thumbnailCache()).set(image.id, image.thumbnailUrl));
      return;
    }
    if (!image.fullUrl) return;
    this.fetchFile(image.fullUrl, image).then(file => {
      const blobUrl = URL.createObjectURL(file);
      this.thumbnailCache.set(new Map(this.thumbnailCache()).set(image.id, blobUrl));
    }).catch(() => { });
  }

  private revokePreviewUrl(): void {
    const url = this.previewUrl();
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  }
}
